import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import {
  Box, Card, CardContent, TextField, Button, Typography, Alert, Avatar, CircularProgress,
} from '@mui/material';
import { PersonAdd as RegisterIcon } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { PASSWORD_RULES_TEXT, passwordProblem, apiErrorMessage } from '../passwordPolicy';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

/** Latest selectable date of birth — the browser refuses anything after it. */
const TODAY = new Date().toISOString().slice(0, 10);

export default function Register() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { loginWithToken } = useAuth();
  const inviteToken = searchParams.get('token') || '';

  const [inviteInfo, setInviteInfo] = useState(null);
  const [validating, setValidating] = useState(true);
  const [invalidMsg, setInvalidMsg] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [dob, setDob] = useState('');
  const [error, setError] = useState('');
  const [accountExists, setAccountExists] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!inviteToken) {
      setValidating(false);
      setInvalidMsg('No invite token provided. Please use the link from your clinic.');
      return;
    }
    axios.get(`${API_URL}/api/v1/auth/invite/${inviteToken}`)
      .then((res) => {
        setInviteInfo(res.data);
        setFullName(res.data.full_name || '');
      })
      .catch((err) => {
        setInvalidMsg(apiErrorMessage(err, 'Invalid or expired invite link.'));
      })
      .finally(() => setValidating(false));
  }, [inviteToken]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    const problem = passwordProblem(password);
    if (problem) {
      setError(problem);
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    // Checked here as well as on the server so the person gets the message beside the
    // field rather than as a 422 after the round trip.
    if (!dob) {
      setError('Please enter your date of birth.');
      return;
    }
    if (dob > TODAY) {
      setError('Date of birth cannot be in the future.');
      return;
    }
    setLoading(true);
    setAccountExists(false);
    try {
      const res = await axios.post(`${API_URL}/api/v1/auth/register/patient`, {
        token: inviteToken,
        password,
        full_name: fullName || undefined,
        date_of_birth: dob,
      });
      loginWithToken(res.data.user);
      navigate('/');
    } catch (err) {
      const detail = apiErrorMessage(err, 'Registration failed.');
      setError(detail);
      if (String(detail).toLowerCase().includes('already exists')) {
        setAccountExists(true);
      }
    } finally {
      setLoading(false);
    }
  };

  if (validating) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (invalidMsg) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3 }}>
        <Card sx={{ maxWidth: 420, width: '100%', textAlign: 'center' }}>
          <CardContent sx={{ p: 4 }}>
            <Typography variant="h5" fontWeight={700} gutterBottom color="error">
              Invalid Invite
            </Typography>
            <Typography color="text.secondary" sx={{ mb: 3 }}>{invalidMsg}</Typography>
            <Button variant="outlined" onClick={() => navigate('/login')}>Go to Login</Button>
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Box sx={{
      minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: 'linear-gradient(180deg, #faf6f0 0%, #f5ebe0 100%)', p: 3,
    }}>
      <Card sx={{
        maxWidth: 460, width: '100%',
        background: 'linear-gradient(145deg, rgba(255,254,251,0.95) 0%, rgba(255,247,237,0.9) 100%)',
        border: '1px solid rgba(180, 83, 9, 0.15)',
        boxShadow: '0 25px 50px -12px rgba(180, 83, 9, 0.2)',
      }}>
        <CardContent sx={{ p: 4, textAlign: 'center' }}>
          <Avatar sx={{ width: 64, height: 64, bgcolor: 'primary.main', mx: 'auto', mb: 2 }}>
            <RegisterIcon sx={{ fontSize: 32 }} />
          </Avatar>
          <Typography variant="h4" fontWeight={700} gutterBottom sx={{
            fontFamily: '"Cormorant Garamond", Georgia, serif', color: '#431407',
          }}>
            Set Up Your Account
          </Typography>
          {inviteInfo?.clinic_name && (
            <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
              You've been invited by <strong>{inviteInfo.clinic_name}</strong>
            </Typography>
          )}
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {inviteInfo?.email}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Create your account to view your visit summaries, follow-up instructions
            and medications — all in one place.
          </Typography>

          {error && <Alert severity="error" sx={{ mb: 2, textAlign: 'left' }}>{error}</Alert>}

          {accountExists && (
            <Button fullWidth variant="outlined" sx={{ mb: 2 }} onClick={() => navigate('/forgot')}>
              Reset my password instead
            </Button>
          )}

          <form onSubmit={handleSubmit}>
            <TextField fullWidth label="Full Name" InputLabelProps={{ shrink: true }} value={fullName}
              onChange={(e) => setFullName(e.target.value)} margin="normal"
              sx={{ '& .MuiOutlinedInput-root': { backgroundColor: 'rgba(255,255,255,0.8)' } }} />
            <TextField fullWidth required label="Date of Birth" type="date"
              InputLabelProps={{ shrink: true }} inputProps={{ max: TODAY }}
              value={dob} onChange={(e) => setDob(e.target.value)} margin="normal"
              helperText="Your clinic uses this to confirm your identity." />
            <TextField fullWidth label="Create Password" type="password" InputLabelProps={{ shrink: true }} value={password}
              onChange={(e) => setPassword(e.target.value)} margin="normal" required autoComplete="new-password"
              helperText={PASSWORD_RULES_TEXT}
              sx={{ '& .MuiOutlinedInput-root': { backgroundColor: 'rgba(255,255,255,0.8)' } }} />
            <TextField fullWidth label="Confirm Password" type="password" InputLabelProps={{ shrink: true }} value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)} margin="normal" required
              sx={{ '& .MuiOutlinedInput-root': { backgroundColor: 'rgba(255,255,255,0.8)' } }} />
            <Button fullWidth type="submit" variant="contained" size="large" disabled={loading}
              sx={{
                mt: 3, py: 1.5, background: 'linear-gradient(135deg, #b45309 0%, #d97706 100%)',
                fontWeight: 700, '&:hover': { background: 'linear-gradient(135deg, #92400e 0%, #b45309 100%)' },
              }}>
              {loading ? 'Creating account...' : 'Create Account'}
            </Button>
          </form>

          <Button sx={{ mt: 2 }} onClick={() => navigate('/login')}>
            Already have an account? Sign in
          </Button>
        </CardContent>
      </Card>
    </Box>
  );
}
